import React, { createContext, useContext, ReactNode } from 'react';

interface WizardContextType {
  activeStep: number;
  setActiveStep: (step: number) => void;
  activeTabIndex: number;
  setActiveTabIndex: (index: number) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  goToStep: (step: number) => void;
}

const WizardContext = createContext<WizardContextType | undefined>(undefined);

export const useWizard = (): WizardContextType => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
};

interface WizardProviderProps {
  children: ReactNode;
  activeStep: number;
  setActiveStep: (step: number) => void;
  activeTabIndex: number;
  setActiveTabIndex: (index: number) => void;
}

export const WizardProvider: React.FC<WizardProviderProps> = ({
  children,
  activeStep,
  setActiveStep,
  activeTabIndex,
  setActiveTabIndex,
}) => {
  const goToNextStep = () => {
    const nextStep = activeStep + 1;
    setActiveStep(nextStep);
    setActiveTabIndex(nextStep);
  };

  const goToPreviousStep = () => {
    const previousStep = activeStep - 1;
    if (previousStep >= 0) {
      setActiveStep(previousStep);
      setActiveTabIndex(previousStep);
    }
  };

  const goToStep = (step: number) => {
    setActiveStep(step);
    setActiveTabIndex(step);
  };

  return (
    <WizardContext.Provider
      value={{
        activeStep,
        setActiveStep,
        activeTabIndex,
        setActiveTabIndex,
        goToNextStep,
        goToPreviousStep,
        goToStep,
      }}
    >
      {children}
    </WizardContext.Provider>
  );
};
